import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Plus, Trash2, AlertCircle
} from 'lucide-react';
import OELCodeEditor, {
 OELContext
} from '../components/OELCodeEditor';
import StepControllers from '../components/StepControllers';
import {
 CallStep, JsonOpcodeStep
} from 'builders/builderOpcode/jsonMode/types';

/**
 * V2StepCall — invoke a definition by ref.
 * Per A-0012.02 SPEC §2.6 + Q4.1 (forward-looking inclusion as 9th kind per
 * SCHEMAS.md §4). If Joi rejects on save, the editor surfaces the error.
 */
const V2StepCall = ({
    step,
    onStepChange,
    definitions = [],
    oelContext,
}: {
    step: CallStep;
    onStepChange: (step: CallStep) => void;
    definitions?: JsonOpcodeStep[];
    oelContext?: OELContext;
}) => {
    const ctx: OELContext = oelContext || {
 inputs: [],
previousStepIds: []
};
    const inputEntries = Object.entries(step.inputs || {}).filter(([k]) => !k.startsWith('x-'));

    const handleRefChange = (ref: string) => {
        onStepChange({
            ...step,
            call: {
 ref
}
        });
    };

    const handleAddInput = () => {
        const newKey = `arg_${inputEntries.length + 1}`;
        onStepChange({
            ...step,
            inputs: {
 ...(step.inputs || {}),
[newKey]: ''
}
        });
    };

    const handleRemoveInput = (key: string) => {
        const newInputs = {
 ...(step.inputs || {})
};
        delete newInputs[key];
        onStepChange({
 ...step,
inputs: newInputs
});
    };

    const handleInputKeyChange = (oldKey: string, newKey: string) => {
        if (newKey === oldKey) return;
        const newInputs: Record<string, string> = {};
        for (const [k, v] of Object.entries(step.inputs || {})) {
            newInputs[k === oldKey ? newKey : k] = String(v);
        }
        onStepChange({
 ...step,
inputs: newInputs
});
    };

    const handleInputValueChange = (key: string, value: string) => {
        onStepChange({
            ...step,
            inputs: {
 ...(step.inputs || {}),
[key]: value
}
        });
    };

    return (
        <div className="space-y-4">
            <div className="rounded-md border border-yellow-200 bg-yellow-50/60 p-2 text-[11px] text-yellow-900">
                <AlertCircle className="mr-1 inline h-3 w-3" />
                <code>call</code> is a 9th kind per SCHEMAS.md §4 (forward-looking).
                If Server Joi rejects on save, surface as a clear error and align Joi.
            </div>

            <div className="space-y-1">
                <Label>Definition Reference <span className="text-destructive">*</span></Label>
                {definitions.length > 0 ? (
                    <Select
                        value={step.call?.ref || ''}
                        onValueChange={handleRefChange}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Pick a definition…" />
                        </SelectTrigger>
                        <SelectContent>
                            {definitions.map((d, i) => {
                                const id = (d as any).id || `def_${i}`;
                                return <SelectItem key={id} value={id}>{id}</SelectItem>;
                            })}
                        </SelectContent>
                    </Select>
                ) : (
                    <Input
                        value={step.call?.ref || ''}
                        onChange={(e) => handleRefChange(e.target.value)}
                        placeholder="Enter definition ID"
                        className="font-mono text-sm"
                    />
                )}
                <p className="text-xs text-muted-foreground">
                    References a step in the opcode's <code>definitions[]</code>.
                </p>
            </div>

            <div className="space-y-2">
                <Label>Arguments</Label>
                <p className="text-xs text-muted-foreground">Pass OEL-resolved values to the called definition.</p>
                {inputEntries.map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                        <Input
                            value={key}
                            onChange={(e) => handleInputKeyChange(key, e.target.value)}
                            placeholder="Argument name"
                            className="flex-1 font-mono text-sm"
                        />
                        <span className="text-muted-foreground">=</span>
                        <div className="flex-1">
                            <OELCodeEditor
                                value={typeof value === 'string' ? value : JSON.stringify(value)}
                                onChange={(v) => handleInputValueChange(key, v)}
                                placeholder="@input.value"
                                oelContext={ctx}
                                templateMode={false}
                            />
                        </div>
                        <Button type="button" size="icon" variant="ghost" onClick={() => handleRemoveInput(key)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={handleAddInput} className="w-full">
                    <Plus className="mr-2 h-4 w-4" /> Add Argument
                </Button>
            </div>

            <StepControllers
                when={step.when}
                forCtrl={step.for}
                repeatUntil={step.repeat_until}
                onWhenChange={(val) => onStepChange({
 ...step,
when: val
})}
                onForChange={(val) => onStepChange({
 ...step,
for: val
})}
                onRepeatUntilChange={(val) => onStepChange({
 ...step,
repeat_until: val
})}
                oelContext={ctx}
            />
        </div>
    );
};

export default V2StepCall;
